import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onSnapshot, query } from "firebase/firestore";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";

// Resolve the Staff doc id that maps to the signed-in user (Staff docs link to
// auth accounts via a `userId` field). Falls back to the auth uid so filtering
// still works even when no Staff record is linked.
const resolveStaffId = (staff, userId) =>
  staff?.find((s) => s.userId === userId)?.id || userId;

const priorityColor = (priority) => {
  switch ((priority || "").toLowerCase()) {
    case "high":
      return "error";
    case "low":
      return "default";
    default:
      return "warning";
  }
};

// How many days ago the order was reported, phrased for the card footer.
const daysOld = (value) => {
  if (!value) return "";
  // reportedTime is stored as a JS Date / Firestore Timestamp
  const date = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "Today";
  if (days === 1) return "1 day old";
  return `${days} days old`;
};

const WorkOrderCard = ({ order, claimed, onClick }) => (
  <Card variant="outlined" sx={{ mb: 1.5 }}>
    <CardActionArea onClick={onClick} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box minWidth={0}>
          {claimed ? (
            <Typography variant="subtitle1" fontWeight={600}>
              Room {order.room || "—"}
            </Typography>
          ) : (
            <Box display="flex" alignItems="center" gap={0.5}>
              <LockOutlinedIcon fontSize="small" color="disabled" />
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                Room hidden
              </Typography>
            </Box>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {order.orderDetails || "No description"}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} ml={1}>
          {order.priority && (
            <Chip
              size="small"
              label={order.priority}
              color={priorityColor(order.priority)}
            />
          )}
          {!order.assignedTo && (
            <Chip size="small" variant="outlined" label="Unassigned" />
          )}
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1.5}>
        <Typography variant="caption" color="text.secondary">
          {order.category || "General"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {daysOld(order.reportedTime)}
        </Typography>
      </Box>
    </CardActionArea>
  </Card>
);

const EmptyState = ({ label }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    py={8}
    color="text.secondary"
  >
    <BuildIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
    <Typography>{label}</Typography>
  </Box>
);

const MaintenanceWorkOrders = ({ userId, staff }) => {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const myStaffId = useMemo(
    () => resolveStaffId(staff, userId),
    [staff, userId]
  );

  useEffect(() => {
    if (!selectedProperty) return undefined;
    setLoading(true);
    const workOrdersRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "workOrders"
    );
    const unsubscribe = onSnapshot(query(workOrdersRef), (snapshot) => {
      const all = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setOrders(all);
      setLoading(false);
    });
    return unsubscribe;
  }, [selectedProperty]);

  // Open work orders assigned to me or not yet assigned to anyone.
  const openOrders = useMemo(
    () =>
      orders.filter(
        (o) =>
          o.completed !== true &&
          o.markedCompleted !== true &&
          (!o.assignedTo ||
            o.assignedTo === myStaffId ||
            o.assignedTo === userId)
      ),
    [orders, myStaffId, userId]
  );

  // Orders I marked complete that are awaiting manager approval.
  const submittedOrders = useMemo(
    () =>
      orders.filter(
        (o) =>
          o.completed !== true &&
          o.markedCompleted === true &&
          (o.markedCompleteBy === myStaffId || o.markedCompleteBy === userId)
      ),
    [orders, myStaffId, userId]
  );

  const visible = tab === 0 ? openOrders : submittedOrders;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Work Orders
      </Typography>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="fullWidth"
      >
        <Tab label={`Open (${openOrders.length})`} />
        <Tab label={`Submitted (${submittedOrders.length})`} />
      </Tabs>

      {visible.length === 0 ? (
        <EmptyState
          label={
            tab === 0
              ? "No open work orders right now"
              : "Nothing awaiting approval"
          }
        />
      ) : (
        visible.map((order) => (
          <WorkOrderCard
            key={order.id}
            order={order}
            claimed={
              !!order.assignedTo &&
              (order.assignedTo === myStaffId || order.assignedTo === userId)
            }
            onClick={() => navigate(`/work-orders/${order.id}`)}
          />
        ))
      )}
    </Box>
  );
};

export default MaintenanceWorkOrders;
